import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import { eventsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const Events = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    event_key: '',
    name: '',
    description: '',
    base_price: 15.00,
    primary_color: '#b83e31',
    secondary_color: '#ffffff',
    text_color: '#ffffff',
    button_color: '#b83e31',
    button_text_color: '#ffffff',
    display_location: 'tickets',
    slot_calculation_type: 'per_booking',
    slots_per_booking: 1,
    slots_per_child: 1.00,
    fixed_date: '',
    hide_date_step: false,
    is_active: true,
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await eventsAPI.list(true);
      if (response.data.success) {
        setEvents(response.data.data);
      } else {
        setError('Keine Events gefunden');
      }
    } catch (err) {
      setError('Fehler beim Laden der Events: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (event = null) => {
    if (event) {
      setEditMode(true);
      setFormData({
        id: event.id,
        event_key: event.event_key,
        name: event.name,
        description: event.description || '',
        base_price: event.base_price || 15.00,
        primary_color: event.primary_color || '#b83e31',
        secondary_color: event.secondary_color || '#ffffff',
        text_color: event.text_color || '#ffffff',
        button_color: event.button_color || '#b83e31',
        button_text_color: event.button_text_color || '#ffffff',
        display_location: event.display_location || 'tickets',
        slot_calculation_type: event.slot_calculation_type || 'per_booking',
        slots_per_booking: event.slots_per_booking || 1,
        slots_per_child: event.slots_per_child || 1.00,
        fixed_date: event.fixed_date || '',
        hide_date_step: event.hide_date_step === 1 || event.hide_date_step === true,
        is_active: event.is_active === 1,
      });
    } else {
      setEditMode(false);
      setFormData({
        id: null,
        event_key: '',
        name: '',
        description: '',
        base_price: 15.00,
        primary_color: '#b83e31',
        secondary_color: '#ffffff',
        text_color: '#ffffff',
        button_color: '#b83e31',
        button_text_color: '#ffffff',
        display_location: 'tickets',
        fixed_date: '',
        hide_date_step: false,
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (editMode) {
        const response = await eventsAPI.update(formData);
        if (response.data.success) {
          setDialogOpen(false);
          loadEvents();
        }
      } else {
        const response = await eventsAPI.create(formData);
        if (response.data.success) {
          setDialogOpen(false);
          loadEvents();
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'event_key', headerName: 'Schlüssel', width: 150 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'description', headerName: 'Beschreibung', width: 250 },
    {
      field: 'base_price',
      headerName: 'Event Preis',
      width: 130,
      valueFormatter: (params) => `CHF ${parseFloat(params.value || 0).toFixed(2)}`,
    },
    {
      field: 'is_active',
      headerName: 'Aktiv',
      width: 100,
      renderCell: (params) => (
        <Switch checked={params.value === 1} disabled />
      ),
    },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => navigate(`/events/${params.row.id}/config`)}
            variant="outlined"
          >
            Konfigurieren
          </Button>
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() => handleOpenDialog(params.row)}
          >
            Bearbeiten
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          Events & Veranstaltungen
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Neues Event
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card>
        <CardContent>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={events}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              loading={loading}
              disableSelectionOnClick
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Event bearbeiten' : 'Neues Event erstellen'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Event Schlüssel"
                value={formData.event_key}
                onChange={(e) => setFormData({ ...formData, event_key: e.target.value })}
                disabled={editMode}
                required
                helperText="z.B. entrance, birthday (keine Leerzeichen)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Event Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Beschreibung"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Event Preis"
                type="number"
                value={formData.base_price}
                onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) })}
                InputProps={{
                  startAdornment: 'CHF',
                }}
                helperText="Preis pro Person für dieses Event"
              />
            </Grid>
            
            {/* Color Configuration Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                Farbkonfiguration
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Primärfarbe"
                type="color"
                value={formData.primary_color}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                helperText="Hauptfarbe für das Event"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sekundärfarbe"
                type="color"
                value={formData.secondary_color}
                onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                helperText="Hintergrundfarbe"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Textfarbe"
                type="color"
                value={formData.text_color}
                onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                helperText="Farbe für Titel und Text"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Button-Farbe"
                type="color"
                value={formData.button_color}
                onChange={(e) => setFormData({ ...formData, button_color: e.target.value })}
                helperText="Farbe für Buttons"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Button-Textfarbe"
                type="color"
                value={formData.button_text_color}
                onChange={(e) => setFormData({ ...formData, button_text_color: e.target.value })}
                helperText="Textfarbe für Buttons"
              />
            </Grid>
            
            {/* Color Preview */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: formData.secondary_color }}>
                <Typography variant="h6" sx={{ color: formData.text_color, fontWeight: 700, mb: 1 }}>
                  Vorschau: {formData.name || 'Event Name'}
                </Typography>
                <Typography variant="body2" sx={{ color: formData.text_color, mb: 2 }}>
                  Dies ist eine Vorschau der Farben für dieses Event.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: formData.button_color,
                    color: formData.button_text_color,
                    '&:hover': {
                      backgroundColor: formData.button_color,
                      opacity: 0.9,
                    },
                  }}
                >
                  Beispiel Button
                </Button>
              </Box>
            </Grid>
            
            {/* Display Location */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Anzeigeort</InputLabel>
                <Select
                  value={formData.display_location}
                  label="Anzeigeort"
                  onChange={(e) => setFormData({ ...formData, display_location: e.target.value })}
                >
                  <MenuItem value="tickets">Tickets Seite</MenuItem>
                  <MenuItem value="birthday">Geburtstage Seite</MenuItem>
                  <MenuItem value="both">Beide Seiten</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                Slot-Berechnung
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Berechnungstyp</InputLabel>
                <Select
                  value={formData.slot_calculation_type}
                  label="Berechnungstyp"
                  onChange={(e) => setFormData({ ...formData, slot_calculation_type: e.target.value })}
                >
                  <MenuItem value="per_booking">Slots pro Buchung</MenuItem>
                  <MenuItem value="per_children">Slots pro Kind</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {formData.slot_calculation_type === 'per_booking' ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Slots pro Buchung"
                  value={formData.slots_per_booking}
                  onChange={(e) => setFormData({ ...formData, slots_per_booking: parseInt(e.target.value) })}
                  inputProps={{ min: 1 }}
                  helperText="Feste Anzahl Slots pro Buchung"
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  step="0.1"
                  label="Slots pro Kind"
                  value={formData.slots_per_child}
                  onChange={(e) => setFormData({ ...formData, slots_per_child: parseFloat(e.target.value) })}
                  inputProps={{ min: 0.1, step: 0.1 }}
                  helperText="Anzahl Slots pro Kind (z.B. 1.5 = 1.5 Slots pro Kind)"
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                Einmaliges Event
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Festes Datum"
                value={formData.fixed_date || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    fixed_date: value,
                    // A hidden Datum step needs a date to fall back on - clearing
                    // the date always turns the toggle back off too.
                    hide_date_step: value ? formData.hide_date_step : false,
                  });
                }}
                InputLabelProps={{ shrink: true }}
                helperText="Nur für Events, die an einem einzigen festen Datum stattfinden (z.B. Halloween)"
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hide_date_step}
                    disabled={!formData.fixed_date}
                    onChange={(e) => setFormData({ ...formData, hide_date_step: e.target.checked })}
                  />
                }
                label="Datum-Schritt für Kunden ausblenden"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Event ist aktiv"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.event_key || !formData.name}
          >
            {saving ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Events;
